import type { IParagraphNode } from "~/models/IParagraphNode";
import { cloudStorageUrl } from "~/utils/cloudStorage";

type Transformer = () => string | null;

export function useNodeTransform(node: Readonly<IParagraphNode>) {
  const props = node.properties;

  const transformers: Record<string, Transformer> = {
    src: () => {
      const src = props.src;
      if (!src) {
        return "";
      }
      if (src.startsWith("http")) {
        return src;
      }

      return cloudStorageUrl(src);
    },
    loading: () => (props.src ? "lazy" : ""),
    tag: () => {
      const tag = props.tag;
      switch (tag) {
        case "#text":
        case "sp":
          return "span";
        // transform to simple div - even paragraphs aren't safe!
        // gesetze im internet uses the p tag even when a block element like a div or even tables are contained within, which HTML doesn't allow
        case "p":
        case "la":
          return "div";
        // return null to skip these nodes
        case "colspec":
          return null;
        default:
          return tag;
      }
    },
    class: () => {
      const classes = [] as string[];
      if (props.list) {
        classes.push("grid", "grid-cols-[auto,1fr]", "gap-4");
      }

      if (props.inline) {
        classes.push("inline");
      }

      if (props.italics) {
        classes.push("italic");
      }

      if (props.border) {
        // see ParagraphPart style tag
        classes.push("bordered");
      }

      if (props.bold) {
        classes.push("font-bold");
      }

      if (props["left-indent"]) {
        classes.push("ml-4");
      }

      if (props.center) {
        classes.push("text-center");
      }

      if (props["has-order"]) {
        classes.push("has-order");
      }

      return classes.join(" ");
    },
  };

  const transformed = {
    ...props,
    ...Object.fromEntries(
      Object.entries(transformers)
        .map(([key, transformer]) => [key, transformer()])
        .filter(([, value]) => value !== "")
    ),
  };

  const { tag, text, ...properties } = transformed;

  return {
    tag,
    text,
    properties,
  };
}
